import { ObservableQuery } from '@apollo/client';
import {
  ArticlesQuery,
  ArticlesQueryVariables,
} from 'lib-knowledge-management-common/graphql';
import { useCallback } from 'react';

import { useInfiniteScroll } from 'hooks/useInfiniteScroll';

export type FetchMoreArticlesFn = ObservableQuery<
  ArticlesQuery,
  ArticlesQueryVariables
>['fetchMore'];

export type UseArticleInfiniteScrollParams = {
  /** Gets assigned to the last row in the table */
  lastRowRef: React.RefObject<HTMLTableRowElement>;
  /** Gets assigned to the wrapping container */
  containerRef: React.RefObject<HTMLDivElement>;
  fetchMore: FetchMoreArticlesFn;
  hasMore: boolean;
  offset: number;
};

/**
 * The articles table's paging: how a page is asked for and merged.
 *
 * When to ask is `useInfiniteScroll`'s, which the suggestion queue shares.
 */
export function useArticleInfiniteScroll({
  lastRowRef,
  containerRef,
  fetchMore,
  hasMore,
  offset,
}: UseArticleInfiniteScrollParams): void {
  const fetchNextPage = useCallback(() => {
    fetchMore({
      variables: { offset },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;

        const clonedPrevious = structuredClone(previous);
        clonedPrevious.articles.query.results = [
          ...clonedPrevious.articles.query.results,
          ...fetchMoreResult.articles.query.results,
        ];

        return clonedPrevious;
      },
    });
  }, [fetchMore, offset]);

  useInfiniteScroll({
    lastRowRef,
    containerRef,
    fetchMore: fetchNextPage,
    hasMore,
    rowCount: offset,
  });
}
